import React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { FilterLabel } from '@lt/components';
import { i18n } from '@lt/localization';

import { getIsMobile } from 'src/store/view/selectors';
import { isArray } from 'lodash';
import FILTER_LIST from './filters';

import {
  changeFlightsFilter,
  clearFlightsFilter,
  getFlightsFilterData,
  minPriceForFilterByTourIdSelector,
  sliderFilters,
} from '../../store/flights-filters';

import type { FilterPanelProps } from './types';
import { applySingleChoiseFilters } from '../../store/flights-filters/actions';

import {
  changeFlightsOneSliderFilter,
  changeFlightsSliderFilter,
} from '../../store/flights-filters/slice';
import { getFlightsCheapestPrice } from '../../store/flights/selectors';

const FlightsFilterPanel = ({
  getFilterData,
  isMobile,
  changeFilter,
  clearFilter,
  changeSingleOption,
  changeSliderFilter,
  changeOneSliderFilter,
  getMinPriceForFilter,
  minPriceForSliderFilters,
}: FilterPanelProps): JSX.Element => (
  <StyledFilterPanel>
    {FILTER_LIST.map((Filter) => {
      const { filterName, getFilterVisibleName, getFilterDescription } = Filter;
      const filterData = getFilterData(filterName);

      if (
        !filterData ||
        (!sliderFilters.includes(filterName) && !isArray(filterData)) ||
        (isArray(filterData) && !filterData?.length)
      )
        return null;

      if (sliderFilters.includes(filterName))
        return (
          <Filter
            data={filterData}
            onClear={clearFilter}
            isMobile={isMobile}
            key={filterName}
            onChange={
              filterName === 'max_flight_duration'
                ? changeOneSliderFilter
                : changeSliderFilter
            }
            minPrice={minPriceForSliderFilters}
            Filter={Filter}
          />
        );

      if (!isArray(filterData)) return null;

      // adding names and descriptions
      const data = filterData.map((filterOptionData) => ({
        ...filterOptionData,
        name: getFilterVisibleName(filterOptionData),
        min_price: getMinPriceForFilter(filterOptionData),
        description:
          getFilterDescription && getFilterDescription(filterOptionData),
      }));

      return (
        <Filter
          data={data}
          isMobile={isMobile}
          onChange={changeFilter}
          onChangeSingleOption={changeSingleOption}
          onClear={clearFilter}
          Filter={Filter}
          key={filterName}
          LabelComponent={StyledFilterLabel}
          isClearButtonInline
        />
      );
    })}
  </StyledFilterPanel>
);

const StyledFilterLabel = styled(FilterLabel).attrs({
  emptyMessage: i18n.t('checkout.filter.empty'),
  subjectToPlural: 'FLIGHTS',
})``;

const StyledFilterPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  margin: 16px 0;
  padding: 12px 8px;
  background-color: var(--white);
  border-radius: var(--border-radius);
`;

const mapPropsToState = (state) => ({
  isMobile: getIsMobile(state),
  getFilterData: (filterName) => getFlightsFilterData(state, filterName),
  getMinPriceForFilter: (filterOptionData) =>
    minPriceForFilterByTourIdSelector(state, filterOptionData),
  minPriceForSliderFilters: getFlightsCheapestPrice(state),
});

const mapDispatchToProps = {
  changeFilter: changeFlightsFilter,
  changeSliderFilter: changeFlightsSliderFilter,
  changeOneSliderFilter: changeFlightsOneSliderFilter,
  changeSingleOption: applySingleChoiseFilters,
  clearFilter: clearFlightsFilter,
};

export default connect(mapPropsToState, mapDispatchToProps)(FlightsFilterPanel);
